import { ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import {
  ALL_TASK_STATUSES,
  replaceStatusFilter,
  type TaskStatusFilterKey,
} from "components/Tasks/taskStatusFilter";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useParams } from "react-router-dom";

import { useTaskCountQueries } from "./useTaskCountQueries";

export enum TaskFilterType {
  Open = "open",
  Overdue = "overdue",
  Suggested = "suggested",
  Completed = "completed",
  AllButCanceled = "allButCanceled",
}

interface TaskStatCardProps {
  icon: string;
  label: string;
  count: number | undefined;
  loading: boolean;
  onClick: () => void;
}

const TaskStatCard = ({ icon, label, count, loading, onClick }: TaskStatCardProps) => {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        width: 200,
        height: 52,
        px: 2,
        borderRadius: 1,
        backgroundColor: "action.selected",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, mb: "2px" }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={60} height={20} />
      ) : (
        <Typography variant="body2" fontWeight="bold">
          {icon} {count ?? 0}
        </Typography>
      )}
    </ButtonBase>
  );
};

export const TaskFilterChips = ({ facilityId }: { facilityId?: string } = {}) => {
  const { tenantId } = useParams();
  const { omnisearch, setOmnisearchImmediate } = useOmnisearch();

  const { allQuery, overdueQuery, completedQuery } = useTaskCountQueries({
    tenantId,
    facilityId,
  });

  const applyStatusFilter = (key: TaskStatusFilterKey, value: string) => {
    setOmnisearchImmediate(replaceStatusFilter(omnisearch, key, value));
  };

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
      <TaskStatCard
        icon="📋"
        label="All Tasks"
        count={allQuery.data?.virtualEvents.count}
        loading={allQuery.loading}
        onClick={() => applyStatusFilter("taskStatus", ALL_TASK_STATUSES)}
      />
      <TaskStatCard
        icon="⏰"
        label="Overdue Tasks"
        count={overdueQuery.data?.virtualEvents.count}
        loading={overdueQuery.loading}
        onClick={() => applyStatusFilter("status", "OVERDUE")}
      />
      <TaskStatCard
        icon="✅"
        label="Completed Tasks"
        count={completedQuery.data?.virtualEvents.count}
        loading={completedQuery.loading}
        onClick={() => applyStatusFilter("taskStatus", "COMPLETED")}
      />
    </Stack>
  );
};
